import React from 'react';
import { Link } from 'react-router-dom';

// Compact alert tile used in the dashboard's headline strip. Renders a count,
// label, optional subtitle, and acts as a link into a filtered list.
//
// Tones:
//   neutral — gray (default)
//   alert   — red (overdue, expired)
//   warn    — amber (action required, aging)
//   ok      — emerald (clean state, paid)
//   accent  — purple (info / primary)
const TONE_CLS = {
  neutral: 'border-white/[0.05] bg-white/[0.015] text-gray-300',
  alert:   'border-red-500/20 bg-red-500/[0.05] text-red-200',
  warn:    'border-amber-500/20 bg-amber-500/[0.05] text-amber-200',
  ok:      'border-emerald-500/20 bg-emerald-500/[0.05] text-emerald-200',
  accent:  'border-[#B0FC38]/25 bg-[#B0FC38]/[0.06] text-[#B0FC38]',
};

const TONE_VALUE_CLS = {
  neutral: 'text-white',
  alert:   'text-red-200',
  warn:    'text-amber-200',
  ok:      'text-emerald-200',
  accent:  'text-white',
};

export default function Tile({
  label,
  value,
  subtitle = null,
  to = null,
  onClick = null,
  tone = 'neutral',
  icon: Icon = null,
  loading = false,
  testid = null,
}) {
  const cls = `block rounded-2xl border p-4 transition-colors ${TONE_CLS[tone] || TONE_CLS.neutral} ${to || onClick ? 'hover:bg-white/[0.03] cursor-pointer' : ''}`;
  const Inner = (
    <>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 opacity-70" />}
      </div>
      {loading ? (
        <div className="h-7 w-16 rounded bg-white/[0.06] animate-pulse" />
      ) : (
        <div className={`text-[22px] font-semibold leading-tight ${TONE_VALUE_CLS[tone] || TONE_VALUE_CLS.neutral}`}>
          {value}
        </div>
      )}
      {subtitle && <div className="text-[11px] opacity-70 mt-0.5">{subtitle}</div>}
    </>
  );
  if (to) {
    return <Link to={to} className={cls} data-testid={testid}>{Inner}</Link>;
  }
  if (onClick) {
    return <button type="button" onClick={onClick} className={`${cls} text-left w-full`} data-testid={testid}>{Inner}</button>;
  }
  return <div className={cls} data-testid={testid}>{Inner}</div>;
}

export function CardShell({ title, action = null, children, className = '', testid = null }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[13px] font-semibold text-white">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}
